import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import React from "react";
import { entityProfileModalBorders, entityProfileModalColors } from "../entityProfileModal.theme";

type EntityProfileSummaryBlockProps = {
  actions?: React.ReactNode;
  children: React.ReactNode;
  title: string;
};

export function EntityProfileSummaryBlock(props: EntityProfileSummaryBlockProps) {
  return (
    <Box>
      <HStack
        bg={entityProfileModalColors.surface}
        borderBottom={entityProfileModalBorders.default}
        justifyContent="space-between"
        minH="46px"
      >
        <Text color={entityProfileModalColors.text} fontSize="24px" fontWeight={300}>
          {props.title}
        </Text>
        {props.actions}
      </HStack>
      <VStack alignItems="stretch" spacing={0}>
        {props.children}
      </VStack>
    </Box>
  );
}

type EntityProfileSummaryRowProps = {
  actions?: React.ReactNode;
  hideBorderBottom?: boolean;
  isClickable?: boolean;
  isHighlighted?: boolean;
  label: string;
  labelActions?: React.ReactNode;
  value: React.ReactNode;
  onClick?: () => void;
};

export function EntityProfileSummaryRow(props: EntityProfileSummaryRowProps) {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      props.onClick?.();
    }
  };

  return (
    <HStack
      borderBottom={props.hideBorderBottom ? "none" : entityProfileModalBorders.default}
      justifyContent="space-between"
      minH="42px"
      spacing={6}
    >
      <VStack alignItems="flex-start" spacing={1}>
        <Text
          _hover={
            props.isClickable
              ? { color: entityProfileModalColors.active, textDecoration: "underline" }
              : undefined
          }
          color={
            props.isHighlighted
              ? entityProfileModalColors.active
              : entityProfileModalColors.labelText
          }
          cursor={props.isClickable ? "pointer" : undefined}
          fontSize="14px"
          fontWeight={700}
          role={props.isClickable ? "button" : undefined}
          tabIndex={props.isClickable ? 0 : undefined}
          onClick={props.onClick}
          onKeyDown={props.isClickable ? handleKeyDown : undefined}
        >
          {props.label}
        </Text>
        {props.labelActions}
      </VStack>
      <HStack flex="1 1 auto" justifyContent="flex-end" minW={0} spacing={3}>
        <Box
          _hover={
            props.isClickable
              ? { color: entityProfileModalColors.active, textDecoration: "underline" }
              : undefined
          }
          color={
            props.isHighlighted ? entityProfileModalColors.active : entityProfileModalColors.muted
          }
          cursor={props.isClickable ? "pointer" : undefined}
          fontSize="14px"
          fontWeight={props.isHighlighted ? 700 : undefined}
          maxW="420px"
          minW={0}
          role={props.isClickable ? "button" : undefined}
          tabIndex={props.isClickable ? 0 : undefined}
          textAlign="right"
          onClick={props.onClick}
          onKeyDown={props.isClickable ? handleKeyDown : undefined}
        >
          {props.value}
        </Box>
        {props.actions}
      </HStack>
    </HStack>
  );
}

type EntityProfileSummaryActionRowProps = {
  children: React.ReactNode;
};

export function EntityProfileSummaryActionRow(props: EntityProfileSummaryActionRowProps) {
  return (
    <HStack
      borderBottom={entityProfileModalBorders.default}
      justifyContent="flex-start"
      minH="42px"
      py={1.5}
    >
      {props.children}
    </HStack>
  );
}
